import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useParams } from 'react-router-dom';

import '../shared/sass/CreateAndUpdateShared.scss';
import { Loader } from '../../components/Loader/Loader.jsx';
import { API_URL } from '../../config.js';
import { BackButton } from '../../components/BackButton/BackButton.jsx';
import { ModalMessage } from '../../components/ModalMessage/ModalMessage.jsx';
import { TextEditor } from '../../components/TextEditor/TextEditor.jsx';
import { Logout } from '../../components/Logout/Logout.jsx';
import {
	studioSchema,
	studioFileValidations,
} from '../shared/js/validations.js';

export const CreateAndUpdateStudio = () => {
	const { id } = useParams();
	const isEditMode = Boolean(id);

	const [loading, setLoading] = useState(false);
	const [error, setError] = useState(null);
	const [success, setSuccess] = useState(null);

	const initialState = {
		category: '',
		title: '',
		image1: null,
		image2: null,
		image3: null,
	};

	const [formData, setFormData] = useState(initialState);
	const [description, setDescription] = useState('');

	useEffect(() => {
		if (isEditMode) {
			const fetchStudio = async () => {
				try {
					setLoading(true);
					const { data } = await axios.get(`${API_URL}/studio/${id}`);
					setFormData({
						category: data.data.category,
						title: data.data.title,
						image1: data.data.image1,
						image2: data.data.image2,
						image3: data.data.image3,
					});
					setDescription(data.data.description || '');
				} catch (error) {
					setError('Error al cargar los datos del estudio');
				} finally {
					setLoading(false);
				}
			};
			fetchStudio();
		}
	}, [id, isEditMode]);

	const handleChange = (event) => {
		const { name, value, files } = event.target;
		setFormData((prevState) => ({
			...prevState,
			[name]: files ? files[0] : value,
		}));
	};

	const handleSubmit = async (event) => {
		event.preventDefault();
		setLoading(true);
		setSuccess(null);
		setError(null);

		try {
			const { category, title, image1, image2, image3 } = formData;

			// Validación Yup
			await studioSchema.validate(
				{ category, title, description },
				{ abortEarly: false }
			);

			// Validación personalizada de imágenes
			const fileErrors = studioFileValidations(formData, isEditMode);
			if (fileErrors.length > 0) {
				setError(fileErrors.join('\n'));
				setLoading(false);
				return;
			}

			const formDataToSend = new FormData();
			formDataToSend.append('category', category);
			formDataToSend.append('title', title);
			formDataToSend.append('description', description);

			if (image1 instanceof File) formDataToSend.append('image1', image1);
			if (image2 instanceof File) formDataToSend.append('image2', image2);
			if (image3 instanceof File) formDataToSend.append('image3', image3);

			const method = isEditMode ? 'patch' : 'post';
			const url = isEditMode
				? `${API_URL}/studio/${id}`
				: `${API_URL}/studio`;

			await axios[method](url, formDataToSend, {
				headers: {
					'Content-Type': 'multipart/form-data',
				},
			});

			setSuccess(
				`Estudio ${isEditMode ? 'actualizado' : 'creado'} con éxito`
			);
			if (!isEditMode) {
				setFormData(initialState);
				setDescription('');
			}
		} catch (err) {
			if (err.name === 'ValidationError') {
				setError(err.errors.join('\n'));
			} else if (
				err.response?.data?.errors &&
				Array.isArray(err.response.data.errors)
			) {
				const messages = err.response.data.errors
					.map((e) => e.msg)
					.join('\n');
				setError(messages);
			} else {
				setError(err.response?.data?.message || err.message);
			}
		} finally {
			setLoading(false);
		}
	};

	return (
		<div className='CreateAndUpdateShared'>
			{loading && <Loader />}
			<div className='container'>
				{isEditMode ? (
					<BackButton
						to='/list-categories-studio'
						text='Categorías Estudio'
					/>
				) : (
					<BackButton to='/dashboard' text='Dashboard' />
				)}

				<form className='CreateAndUpdate-form' onSubmit={handleSubmit}>
					{error && (
						<ModalMessage
							type='error'
							message={error}
							onClose={() => setError(null)}
						/>
					)}
					{success && (
						<ModalMessage
							type='success'
							message={success}
							onClose={() => setSuccess(null)}
						/>
					)}
					<Logout />
					<h3>
						{isEditMode
							? 'EDITAR CATEGORÍA DE ESTUDIO'
							: 'CREAR CATEGORÍA DE ESTUDIO'}
					</h3>

					<label>Categoría:</label>
					<input
						type='text'
						name='category'
						onChange={handleChange}
						value={formData.category}
					/>

					<label>Título:</label>
					<input
						type='text'
						name='title'
						onChange={handleChange}
						value={formData.title}
					/>

					<label>Descripción:</label>
					<TextEditor
						editorState={description}
						setEditorState={setDescription}
					/>

					{['image1', 'image2', 'image3'].map((imageKey, index) => (
						<React.Fragment key={imageKey}>
							<label>{`Imagen ${index + 1}:`}</label>
							<div className='input-container'>
								<input
									type='file'
									name={imageKey}
									onChange={handleChange}
									accept='image/*'
								/>
								{formData[imageKey]?.secure_url && (
									<img
										src={formData[imageKey].secure_url}
										alt={`imagen ${index + 1}`}
									/>
								)}
							</div>
						</React.Fragment>
					))}

					<button className='submit-btn' type='submit'>
						{isEditMode ? 'Actualizar' : 'Crear'} Estudio
					</button>
				</form>
			</div>
		</div>
	);
};
